import type {
	BillPaymentStatus,
	PaymentDocumentKind,
	PaymentSideSummary,
	PaymentStatus,
	PaymentSum,
} from "@ravnar/api-contract";

import type { Tone } from "../components/ui/tone";
import { formatMoney } from "../lib/format";
import { byCashCurrency, type CashPosition, type CurrencyTotal } from "./revolut";

/** The sides, the statuses, the reasons and their labels come from the API contract. */
export {
	PAYMENT_STATUS_LABELS,
	PAYMENT_STATUSES,
	SETTLEMENT_NOTE_MAX_LENGTH,
	SETTLEMENT_REASON_LABELS,
	SETTLEMENT_REASONS,
	type PaymentSide,
	type PaymentStatus,
} from "@ravnar/api-contract";

/**
 * The tone of each status's pill: what is overdue in the colour that asks for a look, what is still to be paid in the
 * quiet blue of what is in hand, what is paid in green, and what a person settled grey.
 */
export const PAYMENT_STATUS_TONES: Readonly<Record<PaymentStatus, Tone>> = {
	overdue: "alert",
	open: "info",
	paid: "ok",
	settled: "neutral",
};

/**
 * The tone of each draft payment's pill: one Revolut did not answer for in the colour that asks for a look, one that
 * waits or is on its way in blue, one transferred in green, one that failed red, and one deleted grey.
 */
export const BILL_PAYMENT_TONES: Readonly<Record<BillPaymentStatus, Tone>> = {
	sending: "warn",
	awaiting: "info",
	approved: "info",
	completed: "ok",
	failed: "alert",
	deleted: "neutral",
};

/** What each followed kind is called in a list, and at the head of its drawer. */
export const PAYMENT_DOCUMENT_KIND_LABELS: Readonly<Record<PaymentDocumentKind, string>> = {
	"vendor-invoice": "Vendor bill",
	"reimbursement-claim": "Reimbursement claim",
	"customer-invoice": "Invoice",
};

/**
 * Sums of money as one total per currency, in `byCashCurrency` order: the primary currency first, then the rest by
 * code — the order a figure that cannot be converted lists them in.
 */
export function currencyTotals(sums: readonly PaymentSum[], primary: string | undefined): CurrencyTotal[] {
	const totals = new Map<string, number>();
	for (const { amount, currency } of sums) {
		totals.set(currency, (totals.get(currency) ?? 0) + Number(amount));
	}
	const order = byCashCurrency(primary);
	return [...totals]
		.map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }))
		.toSorted((a, b) => order(a.currency, b.currency));
}

/** What a side still has to pay or be paid — every open and overdue document — per currency, of one kind or all. */
export function outstandingTotals(
	side: PaymentSideSummary,
	primary: string | undefined,
	kind?: PaymentDocumentKind,
): CurrencyTotal[] {
	return currencyTotals(
		side.outstanding.filter(sum => kind === undefined || sum.kind === kind),
		primary,
	);
}

/** How many of a side's documents are still to be paid: the open and the overdue. */
export function unpaidCount(side: PaymentSideSummary): number {
	return side.counts.open + side.counts.overdue;
}

/**
 * A sum as one line of a card: one currency's total exact, several converted into the primary currency — "≈" when
 * anything was converted from another currency — or the currencies counted when nothing converts them; undefined when
 * there is nothing.
 */
export function figureOf(position: CashPosition): string | undefined {
	switch (position.kind) {
		case "none": {
			return;
		}
		case "single": {
			return formatMoney(position.total.amount, position.total.currency);
		}
		case "converted": {
			const { approximate, conversion } = position;
			return `${approximate ? "≈ " : ""}${formatMoney(conversion.total, conversion.target)}`;
		}
		case "unconverted": {
			return `${String(position.totals.length)} currencies`;
		}
	}
}
